import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/lib/supabase';
import { useDashboardData, calculateStackCompliance } from '@/hooks/useDashboardData';
import { AppShell3 } from '@/components/app-shell-3';
import { ProjectCard } from '@/components/ProjectCard';
import { StackCard } from '@/components/StackCard';
import { DraggableProjectCard } from '@/components/DraggableProjectCard';
import { DraggableStackCard } from '@/components/DraggableStackCard';
import { SortableProjectCard } from '@/components/SortableProjectCard';
import { SortableStackCard } from '@/components/SortableStackCard';
import { ProjectListItem } from '@/components/ProjectListItem';
import { FilterBar } from '@/components/FilterBar';
import { StackSettingsModal } from '@/components/StackSettingsModal';
import { StackViewModal } from '@/components/StackViewModal';
import { ProjectCreationModal } from '@/components/ProjectCreationModal';
import { CombineStacksModal } from '@/components/CombineStacksModal';
import { ProjectSettingsModal } from '@/components/ProjectSettingsModal';
import { QuickActions } from '@/components/QuickActions';
import { EmptySpacesState } from '@/components/EmptySpacesState';
import InviteAdminModal from '@/components/InviteAdminModal';
import { DashboardSubnav } from '@/components/DashboardSubnav';
import { SpacesImportExportModal } from '@/components/SpacesImportExportModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy
} from '@dnd-kit/sortable';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Spinner } from '@/components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Trash2, AlertTriangle, Pin, Archive, RotateCcw } from 'lucide-react';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, company, userProfile, userRole, loading } = useAuth();
  usePageTitle('Spaces');
  const permissions = usePermissions([
    'spaces.viewAll',
    'spaces.create',
    'users.invite'
  ]);

  // Use optimized dashboard data hook (reduces ~125+ API calls to 1-2)
  const {
    loadDashboardData: loadOptimizedData,
    loadMoreDashboardData: loadMoreOptimized,
    loading: dashboardDataLoading,
    loadingMore: dashboardLoadingMore
  } = useDashboardData();

  const [projects, setProjects] = useState([]);
  // Separate state for archived spaces (background preloaded)
  const [archivedSpaces, setArchivedSpaces] = useState([]);
  const [archivedStacks, setArchivedStacks] = useState([]);
  const [archivedLoaded, setArchivedLoaded] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Easter egg state
  const [showEasterEgg, setShowEasterEgg] = useState(false);
  const gridClickCount = useRef(0);
  const gridClickTimer = useRef(null);
  const [projectOrder, setProjectOrder] = useState([]); // Track display order of project IDs
  const [stacks, setStacks] = useState([]);
  const [stackOrder, setStackOrder] = useState([]); // Track display order of stack IDs
  const [combinedOrder, setCombinedOrder] = useState([]); // Track combined order of projects and stacks
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'a-z', 'z-a', 'stacks', 'projects', 'pinned', 'archived'
  const [searchQuery, setSearchQuery] = useState(''); // Search filter
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [pendingStackProjects, setPendingStackProjects] = useState(null);
  const [isStackSettingsOpen, setIsStackSettingsOpen] = useState(false);
  const [selectedStack, setSelectedStack] = useState(null);
  const [editingStack, setEditingStack] = useState(null); // Track stack being edited
  const [isStackViewOpen, setIsStackViewOpen] = useState(false);
  const [isProjectSettingsOpen, setIsProjectSettingsOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectSettingsMode, setProjectSettingsMode] = useState('details'); // 'details' or 'access'
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isImportExportModalOpen, setIsImportExportModalOpen] = useState(false);
  const [dashboardLoaded, setDashboardLoaded] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const [dashboardSettings, setDashboardSettings] = useState(null); // Cache settings for pagination
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [dragOverlayAnimated, setDragOverlayAnimated] = useState(false);
  const [showDragOverlay, setShowDragOverlay] = useState(false);
  const [isReversing, setIsReversing] = useState(false);
  const [disbandingStackId, setDisbandingStackId] = useState(null);
  const [inCombineZone, setInCombineZone] = useState(false); // Position-based: cursor in center of target
  const [combineReady, setCombineReady] = useState(false); // Dwell threshold reached, will combine on drop
  const [dwellProgress, setDwellProgress] = useState(0); // 0-100 progress toward combine confirmation
  const skipNextSaveRef = useRef(false); // Flag to skip the next reactive save

  // Zone detection and dwell refs
  const DWELL_THRESHOLD = 400; // ms in combine zone to confirm combine intent
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const dwellStartTimeRef = useRef(null);
  const dwellIntervalRef = useRef(null);
  const [isCombineStacksModalOpen, setIsCombineStacksModalOpen] =
    useState(false);
  const [combiningStacks, setCombiningStacks] = useState({
    stack1: null,
    stack2: null
  });
  const [isSelectionMode, setIsSelectionMode] = useState(false); // Bulk selection mode
  const [selectedSpaceIds, setSelectedSpaceIds] = useState([]); // Selected space IDs for bulk actions
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // Delete confirmation modal
  const [isDeleting, setIsDeleting] = useState(false); // Deleting state for bulk delete

  // Pagination state
  const [hasMore, setHasMore] = useState(true); // Whether there are more spaces to load
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Loading more spaces state
  const [currentOffset, setCurrentOffset] = useState(0); // Current pagination offset
  const SPACES_PER_PAGE = 24; // Number of spaces to load per batch
  const loadingRef = useRef(false); // Ref to prevent concurrent loads
  const hasMoreRef = useRef(true); // Ref to track hasMore state synchronously
  const isLoadingMoreRef = useRef(false); // Ref to track loading state synchronously

  // Note: Particle and card glow animations are now in index.css

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8 // Require 8px movement before drag starts
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const breadcrumbs = [];

  // Load dashboard data using OPTIMIZED single RPC call
  useEffect(() => {
    const loadDashboardDataOptimized = async () => {
      if (loading || !user || !company?.id || !userProfile?.id || userRole === null) {
        return;
      }

      try {
        setDashboardError(null);
        const result = await loadOptimizedData({
          companyId: company.id,
          userId: userProfile.id,
          includeArchived: true,
          limit: SPACES_PER_PAGE,
          offset: 0,
          search: searchQuery || null
        });

        if (!result) {
          setProjects([]);
          setProjectOrder([]);
          setStacks([]);
          setStackOrder([]);
          setArchivedSpaces([]);
          setArchivedStacks([]);
          setDashboardLoaded(true);
          setArchivedLoaded(true);
          setHasMore(false);
          hasMoreRef.current = false;
          return;
        }

        setProjects(result.projects);
        setProjectOrder(result.projectOrder);
        setStacks(result.stacks);
        setStackOrder(result.stackOrder);
        setCombinedOrder(result.combinedOrder || []);
        setArchivedSpaces(result.archivedProjects);
        setArchivedStacks(result.archivedStacks);
        setDashboardSettings(result.settings); // Cache settings for pagination
        setHasMore(result.hasMore);
        hasMoreRef.current = result.hasMore;
        setCurrentOffset(result.projects.length);
        setDashboardLoaded(true);
        setArchivedLoaded(true);
      } catch (error) {
        console.error('Dashboard load error:', error);
        setDashboardError(`Failed to load dashboard: ${error.message}`);
      }
    };

    loadDashboardDataOptimized();
  }, [company?.id, userProfile?.id, loading, user, userRole, loadOptimizedData]);
  // Note: activeFilter removed from dependencies - switching filters now uses cached data for instant switching

  // NOTE: Archived spaces are now loaded in the main optimized RPC call above
  // This eliminates the need for a separate background preload
  // The legacy useEffect that loaded archived spaces separately has been removed
  // to reduce API calls from ~125+ to 1-2

  // Separate useEffect for search query changes - OPTIMIZED
  const searchTimeoutRef = useRef(null);
  useEffect(() => {
    // Don't trigger on initial mount
    if (!dashboardLoaded) return;

    // Debounce search - wait 500ms after user stops typing
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setCurrentOffset(0);
      setHasMore(true);
      hasMoreRef.current = true;

      if (!company?.id || !userProfile?.id || userRole === null) return;

      try {
        setDashboardError(null);

        // Use optimized single RPC call for search
        const result = await loadOptimizedData({
          companyId: company.id,
          userId: userProfile.id,
          includeArchived: true,
          limit: SPACES_PER_PAGE,
          offset: 0,
          search: searchQuery || null
        });

        if (!result) {
          setProjects([]);
          setHasMore(false);
          hasMoreRef.current = false;
          return;
        }

        // Update state with search results
        setProjects(result.projects);
        setProjectOrder(result.projectOrder);
        setStacks(result.stacks);
        setStackOrder(result.stackOrder);
        setCombinedOrder(result.combinedOrder || []);
        setArchivedSpaces(result.archivedProjects);
        setArchivedStacks(result.archivedStacks);
        setHasMore(result.hasMore);
        hasMoreRef.current = result.hasMore;
        setCurrentOffset(result.projects.length);
      } catch (error) {
        console.error('Search error:', error);
        setDashboardError(`Failed to search: ${error.message}`);
      }
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, dashboardLoaded, company?.id, userProfile?.id, userRole, loadOptimizedData]);

  // Load more spaces function for infinite scroll - USES OPTIMIZED RPC
  const loadMoreSpaces = useCallback(async () => {
    if (
      loadingRef.current ||
      isLoadingMore ||
      !hasMore ||
      !company?.id ||
      !userProfile?.id
    ) {
      return;
    }

    loadingRef.current = true;
    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;

    try {
      // Use optimized RPC for pagination (same as initial load)
      const result = await loadMoreOptimized({
        companyId: company.id,
        userId: userProfile.id,
        includeArchived: activeFilter === 'archived',
        limit: SPACES_PER_PAGE,
        offset: currentOffset,
        search: searchQuery || null,
        existingSettings: dashboardSettings // Reuse cached settings
      });

      if (!result) {
        setHasMore(false);
        hasMoreRef.current = false;
        return;
      }

      // Update state
      const newOffset = currentOffset + result.projects.length;
      setCurrentOffset(newOffset);
      setHasMore(result.hasMore);
      hasMoreRef.current = result.hasMore;
      setProjects(prevProjects => [...prevProjects, ...result.projects]);

    } catch (error) {
      console.error('Load more error:', error);
    } finally {
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
      loadingRef.current = false;
    }
  }, [
    currentOffset,
    hasMore,
    isLoadingMore,
    company?.id,
    userProfile?.id,
    activeFilter,
    searchQuery,
    SPACES_PER_PAGE,
    loadMoreOptimized,
    dashboardSettings
  ]);

  // Manual load more - no intersection observer for now

  // Memoized save trigger - only recomputes when dependencies actually change
  const saveTrigger = useMemo(() => {
    return JSON.stringify({
      stacksData: stacks.map(s => ({
        id: s.id,
        isPinned: s.isPinned,
        projectIds: s.projects?.map(p => p.id) || [],
        color: s.color
      })),
      projectsPinned: projects.filter(p => p.isPinned).map(p => p.id),
      projectColors: projects.reduce((acc, p) => {
        if (p.color) acc[p.id] = p.color;
        return acc;
      }, {}),
      projectOrder,
      stackOrder,
      combinedOrder
    });
  }, [stacks, projects, projectOrder, stackOrder, combinedOrder]);

  // Expose projects data to ChatFlows widget
  useEffect(() => {
    if (projects && projects.length > 0) {
      window.__CHATFLOWS_PROJECTS_DATA__ = projects.map(p => ({
        id: p.id,
        name: p.name,
        description: p.description
      }));
    } else {
      window.__CHATFLOWS_PROJECTS_DATA__ = [];
    }
  }, [projects]);

  // Save dashboard settings whenever stacks or project colors change
  useEffect(() => {
    // Skip this save if we just did a manual save (e.g., from drag-and-drop)
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }

    if (dashboardLoaded && userProfile?.id && company?.id) {
      const timeoutId = setTimeout(async () => {
        try {
          // Prepare project colors object
          const projectColors = {};

          // Add colors from individual projects
          projects.forEach(project => {
            if (project.color) {
              projectColors[project.id] = project.color;
            }
          });

          // Add colors from stacked projects
          stacks.forEach(stack => {
            stack.projects.forEach(project => {
              if (project.color) {
                projectColors[project.id] = project.color;
              }
            });
          });

          // Prepare stacks data (without the full project objects, just IDs)
          const stacksData = stacks.map(stack => ({
            id: stack.id,
            name: stack.name,
            image: stack.image,
            projectIds: stack.projects.map(p => p.id),
            createdAt: stack.createdAt,
            isPinned: stack.isPinned || false, // Include pinned status
            color: stack.color // Include color
          }));

          // Collect all pinned project IDs
          const pinnedProjects = projects
            .filter(p => p.isPinned)
            .map(p => p.id);

          // Convert order arrays to {id, order} format
          const projectOrderData = projectOrder.map((id, index) => ({
            id,
            order: index
          }));
          const stackOrderData = stackOrder.map((id, index) => ({
            id,
            order: index
          }));
          // Combined order for mixed project/stack ordering
          const combinedOrderData = combinedOrder.map((id, index) => ({
            id,
            order: index
          }));

          const { error } = await supabase
            .from('user_dashboard_settings')
            .upsert(
              {
                user_id: userProfile.id,
                company_id: company?.id, // Include company_id
                stacks: stacksData,
                project_colors: projectColors,
                pinned_projects: pinnedProjects, // Save pinned projects
                project_order: projectOrderData, // Save the project order with explicit indices
                stack_order: stackOrderData, // Save the stack order with explicit indices
                combined_order: combinedOrderData, // Save combined order for mixed project/stack display
                layout_settings: {} // For future use
              },
              {
                onConflict: 'user_id,company_id' // Update constraint to match both
              }
            );

          if (error) {
            console.error('Save settings error:', error);
          }
        } catch (error) {
          console.error('Save settings error:', error);
        }
      }, 500); // Debounce saves by 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [saveTrigger, dashboardLoaded, userProfile?.id, company?.id]);

  // Project types for selection
  const projectTypes = [
    'Safety Compliance',
    'Training Records',
    'Core System',
    'General Team Hub',
    'Document Management',
    'Communications',
    'Mobile Communications',
    'Quality Control',
    'Risk Assessment',
    'Emergency Response'
  ];

  // Handle create new project
  const handleCreateProject = async projectData => {
    if (projectData.name && company?.id && userProfile?.id) {
      try {
        // Create the project with visibility settings
        const { data: createdProject, error: projectError } = await supabase
          .from('projects')
          .insert({
            company_id: company.id,
            name: projectData.name,
            type: projectData.type || 'General',
            description: projectData.description || null,
            location: projectData.location || null,
            industry: projectData.industry || null,
            avatar: projectData.avatar || null,
            tools: JSON.stringify(projectData.tools),
            schedule: JSON.stringify(projectData.schedule),
            visibility_type: projectData.visibility_type || 'invite_only'
          })
          .select()
          .single();

        if (projectError) {
          console.error('Error creating project:', projectError);
          return;
        }

        // Then, create space_members records
        const membersToInsert = [];

        // Always add the creator as owner
        membersToInsert.push({
          project_id: createdProject.id,
          user_id: userProfile.id,
          role: 'Owner',
          created_by: userProfile.id
        });

        // If invite_only, add the invited team members
        if (
          projectData.visibility_type === 'invite_only' &&
          projectData.teamMembers?.length > 0
        ) {
          projectData.teamMembers.forEach(member => {
            // Don't add the creator again if they're in the team list
            if (member.id !== userProfile.id) {
              membersToInsert.push({
                project_id: createdProject.id,
                user_id: member.id,
                role: 'Member',
                created_by: userProfile.id
              });
            }
          });
        }

        // Insert all space members
        const { error: membersError } = await supabase
          .from('space_members')
          .insert(membersToInsert);

        if (membersError) {
          console.error('Error adding project members:', membersError);
          // Note: Project was created but members failed - might want to handle this
        }

        // Transform database project to match UI format
        const project = {
          id: createdProject.id,
          name: createdProject.name,
          type: createdProject.type,
          description: createdProject.description,
          location: createdProject.location,
          industry: createdProject.industry,
          avatar: createdProject.avatar,
          tools: projectData.tools,
          schedule: projectData.schedule,
          teamMembers: projectData.teamMembers || [],
          matrixCount: Math.floor(Math.random() * 5) + 1,
          createdAt: new Date(createdProject.created_date),
          visibility_type: createdProject.visibility_type
        };

        setProjects([project, ...projects]); // Add to beginning
        setIsCreateModalOpen(false);
      } catch (error) {
        console.error('Error in handleCreateProject:', error);
      }
    }
  };

  // Handle importing spaces
  const handleImportSpaces = async spaces => {
    if (!company?.id || !userProfile?.id) return;

    try {
      // Batch insert all projects at once
      const projectInserts = spaces.map(spaceData => ({
        company_id: company.id,
        name: spaceData.name,
        type: spaceData.type || 'Project',
        description: spaceData.description || null,
        location: spaceData.location || null,
        industry: spaceData.industry || null,
        visibility_type: spaceData.visibility_type || 'invite_only',
        matrix_name: spaceData.matrix_name || null,
        custom_fields: spaceData.custom_fields || {},
        tools: spaceData.tools || {}
      }));

      const { data: createdProjects, error: projectsError } = await supabase
        .from('projects')
        .insert(projectInserts)
        .select();

      if (projectsError) {
        console.error('Error creating projects:', projectsError);
        throw projectsError;
      }

      // Build a map of team members by space index for lookup
      const memberInserts = [];
      createdProjects.forEach((createdProject, index) => {
        const spaceData = spaces[index];

        if (spaceData.teamMembers && spaceData.teamMembers.length > 0) {
          // Add each team member for this space
          spaceData.teamMembers.forEach(member => {
            memberInserts.push({
              project_id: createdProject.id,
              user_id: member.id,
              role: member.role || 'Member',
              created_by: userProfile.id
            });
          });
        } else {
          // If no team members specified, add creator as owner
          memberInserts.push({
            project_id: createdProject.id,
            user_id: userProfile.id,
            role: 'Owner',
            created_by: userProfile.id
          });
        }
      });

      // Batch insert all space members at once
      if (memberInserts.length > 0) {
        const { error: membersError } = await supabase
          .from('space_members')
          .insert(memberInserts);

        if (membersError) {
          console.error('Error adding space members:', membersError);
        }
      }

      // Transform to UI format
      const createdSpaces = createdProjects.map(createdProject => ({
        id: createdProject.id,
        name: createdProject.name,
        type: createdProject.type,
        description: createdProject.description,
        location: createdProject.location,
        industry: createdProject.industry,
        avatar: createdProject.avatar,
        tools: [],
        schedule: {},
        teamMembers: [],
        matrixCount: 0,
        createdAt: new Date(createdProject.created_date),
        visibility_type: createdProject.visibility_type,
        matrix_name: createdProject.matrix_name
      }));

      // Add all imported spaces to the beginning of the list
      setProjects([...createdSpaces, ...projects]);
    } catch (error) {
      console.error('Error importing spaces:', error);
      throw error;
    }
  };

  // Get recent projects (use saved order or sort by created date, exclude pinned)
  const getRecentProjects = () => {
    const unpinnedProjects = projects.filter(project => !project.isPinned);

    // If we have a saved order, use it
    if (projectOrder.length > 0) {
      const orderedProjects = [];
      projectOrder.forEach(id => {
        const project = unpinnedProjects.find(p => p.id === id);
        if (project) orderedProjects.push(project);
      });
      // Add any projects not in the order (new ones) at the end
      unpinnedProjects.forEach(project => {
        if (!orderedProjects.find(p => p.id === project.id)) {
          orderedProjects.push(project);
        }
      });
      return orderedProjects;
    }

    // Default: sort by created date
    return unpinnedProjects.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  };

  // Get pinned projects
  const getPinnedProjects = () => {
    return projects.filter(project => project.isPinned);
  };

  // Get all items (projects and stacks) to display in All Spaces (excluding pinned in grid view)
  // Memoized to prevent unnecessary recalculations on every render
  const allSpaceItems = useMemo(() => {
    // If viewing archived spaces, use the preloaded archived data
    if (activeFilter === 'archived') {
      return [
        ...archivedSpaces.map(p => ({ ...p, type: 'project' })),
        ...archivedStacks.map(s => ({ ...s, type: 'stack' }))
      ];
    }

    // Otherwise use the regular active (non-archived) data
    // In list view, show everything. In grid view, exclude pinned items
    let allItems = [];

    if (viewMode === 'list') {
      // Special handling for projects filter - include projects from stacks
      if (activeFilter === 'projects') {
        // Get all standalone projects
        const standaloneProjects = projects.map(p => ({
          ...p,
          type: 'project',
          stackName: null
        }));

        // Get all projects from within stacks
        const projectsInStacks = stacks.flatMap(stack =>
          stack.projects.map(p => ({
            ...p,
            type: 'project',
            stackName: stack.name // Add stack name for display
          }))
        );

        allItems = [...standaloneProjects, ...projectsInStacks];
      } else {
        allItems = [
          ...projects.map(p => ({ ...p, type: 'project' })),
          ...stacks.map(s => ({ ...s, type: 'stack' }))
        ];
      }
    } else {
      // For grid view, respect the saved order
      const unpinnedProjects = projects.filter(p => !p.isPinned);
      const unpinnedStacks = stacks.filter(s => !s.isPinned);

      // Use combinedOrder if available for mixed project/stack ordering
      if (combinedOrder.length > 0) {
        const orderedItems = [];
        combinedOrder.forEach(itemId => {
          const isStack = itemId.toString().startsWith('stack-');
          if (isStack) {
            const stackId = itemId.toString().replace('stack-', '');
            const stack = unpinnedStacks.find(s => s.id.toString() === stackId);
            if (stack) orderedItems.push({ ...stack, type: 'stack' });
          } else {
            const project = unpinnedProjects.find(p => p.id.toString() === itemId.toString());
            if (project) orderedItems.push({ ...project, type: 'project' });
          }
        });
        // Add any new items not in the combined order (new projects/stacks at the front)
        unpinnedProjects.forEach(p => {
          if (!orderedItems.find(item => item.type === 'project' && item.id === p.id)) {
            orderedItems.unshift({ ...p, type: 'project' });
          }
        });
        unpinnedStacks.forEach(s => {
          if (!orderedItems.find(item => item.type === 'stack' && item.id === s.id)) {
            orderedItems.unshift({ ...s, type: 'stack' });
          }
        });
        allItems = orderedItems;
      } else {
        // Fall back to separate project/stack ordering (backward compatibility)
        // Order projects based on projectOrder
        let orderedProjects = unpinnedProjects;
        if (projectOrder.length > 0) {
          const ordered = [];
          projectOrder.forEach(id => {
            const project = unpinnedProjects.find(p => p.id === id);
            if (project) ordered.push(project);
          });
          // Add any new projects not in the order
          unpinnedProjects.forEach(p => {
            if (!ordered.find(op => op.id === p.id)) {
              ordered.push(p);
            }
          });
          orderedProjects = ordered;
        }

        // Order stacks based on stackOrder
        let orderedStacks = unpinnedStacks;
        if (stackOrder.length > 0) {
          const ordered = [];
          stackOrder.forEach(id => {
            const stack = unpinnedStacks.find(s => s.id === id);
            if (stack) ordered.push(stack);
          });
          // Add any new stacks not in the order
          unpinnedStacks.forEach(s => {
            if (!ordered.find(os => os.id === s.id)) {
              ordered.push(s);
            }
          });
          orderedStacks = ordered;
        }

        allItems = [
          ...orderedProjects.map(p => ({ ...p, type: 'project' })),
          ...orderedStacks.map(s => ({ ...s, type: 'stack' }))
        ];
      }
    }

    // Remove duplicates by ID (in case of stale state)
    const uniqueItems = allItems.filter(
      (item, index, self) => index === self.findIndex(t => t.id === item.id)
    );

    // Apply filters
    let filteredItems = [...uniqueItems];

    switch (activeFilter) {
      case 'a-z':
        filteredItems.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'z-a':
        filteredItems.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'stacks':
        filteredItems = filteredItems.filter(item => item.type === 'stack');
        break;
      case 'projects':
        // Already handled above - just sort
        filteredItems.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'pinned':
        filteredItems = filteredItems.filter(item => item.isPinned);
        break;
      default: // 'all'
        // In grid view, if we have projectOrder/stackOrder, preserve that order
        // Only sort by date if we don't have a custom order or in list view
        if (
          viewMode === 'grid' &&
          (projectOrder.length > 0 || stackOrder.length > 0)
        ) {
          // Keep the order from projectOrder/stackOrder (already applied above)
        } else {
          // Sort by date (list view or no custom order)
          filteredItems.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.created_date);
            const dateB = new Date(b.createdAt || b.created_date);
            return dateB - dateA;
          });
        }
    }

    // Note: Search filtering is now handled server-side in the database query
    // No need to filter locally by searchQuery anymore

    // IN LIST VIEW: Always move pinned items to the top
    if (viewMode === 'list') {
      const pinnedItems = filteredItems.filter(item => item.isPinned);
      const unpinnedItems = filteredItems.filter(item => !item.isPinned);
      filteredItems = [...pinnedItems, ...unpinnedItems];
    }

    return filteredItems;
  }, [
    projects,
    stacks,
    archivedSpaces,
    archivedStacks,
    projectOrder,
    stackOrder,
    combinedOrder,
    viewMode,
    activeFilter
  ]);

  // Memoized pinned items (both projects and stacks) - prevents recalculation on every render
  const pinnedItems = useMemo(() => {
    const pinnedProjects = projects.filter(p => p.isPinned);
    const pinnedStacks = stacks.filter(s => s.isPinned);

    // Order pinned projects based on projectOrder
    let orderedPinnedProjects = pinnedProjects;
    if (projectOrder.length > 0) {
      const ordered = [];
      projectOrder.forEach(id => {
        const project = pinnedProjects.find(p => p.id === id);
        if (project) ordered.push(project);
      });
      // Add any pinned projects not in the order
      pinnedProjects.forEach(p => {
        if (!ordered.find(op => op.id === p.id)) {
          ordered.push(p);
        }
      });
      orderedPinnedProjects = ordered;
    }

    // Order pinned stacks based on stackOrder
    let orderedPinnedStacks = pinnedStacks;
    if (stackOrder.length > 0) {
      const ordered = [];
      stackOrder.forEach(id => {
        const stack = pinnedStacks.find(s => s.id === id);
        if (stack) ordered.push(stack);
      });
      // Add any pinned stacks not in the order
      pinnedStacks.forEach(s => {
        if (!ordered.find(os => os.id === s.id)) {
          ordered.push(s);
        }
      });
      orderedPinnedStacks = ordered;
    }

    return [
      ...orderedPinnedProjects.map(p => ({ ...p, type: 'project' })),
      ...orderedPinnedStacks.map(s => ({ ...s, type: 'stack' }))
    ];
  }, [projects, stacks, projectOrder, stackOrder]);

  // Get sortable item IDs for SortableContext (uses memoized pinnedItems)
  const sortableItemIds = useMemo(() => {
    return pinnedItems.map(item =>
      item.type === 'stack' ? `stack-${item.id}` : item.id.toString()
    );
  }, [pinnedItems]);

  // Simple collision detection - just use closestCenter for stacking
  const customCollisionDetection = closestCenter;

  // Handle drag start for visual feedback - memoized to prevent re-renders
  const handleDragStart = useCallback(event => {
    setActiveId(event.active.id);
    setShowDragOverlay(true);
    setDragOverlayAnimated(false);
    setIsReversing(false);
    setInCombineZone(false);
    setCombineReady(false);
    setDwellProgress(0);
    dwellStartTimeRef.current = null;
  }, []);

  // Track mouse position during drag for zone detection
  useEffect(() => {
    if (!activeId) return;

    const handleMouseMove = (e) => {
      mousePositionRef.current = { x: e.clientX, y: e.clientY };
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [activeId]);

  // Dwell timer: runs when in combine zone to confirm intent
  useEffect(() => {
    if (!activeId || !inCombineZone || !overId) {
      // Not in combine zone - clear timer and reset
      if (dwellIntervalRef.current) {
        clearInterval(dwellIntervalRef.current);
        dwellIntervalRef.current = null;
      }
      if (!inCombineZone) {
        setDwellProgress(0);
        setCombineReady(false);
        dwellStartTimeRef.current = null;
      }
      return;
    }

    // Start dwell timer when entering combine zone
    if (!dwellStartTimeRef.current) {
      dwellStartTimeRef.current = Date.now();
    }

    // Update progress every 30ms for smooth animation
    dwellIntervalRef.current = setInterval(() => {
      if (dwellStartTimeRef.current) {
        const elapsed = Date.now() - dwellStartTimeRef.current;
        const progress = Math.min(100, (elapsed / DWELL_THRESHOLD) * 100);
        setDwellProgress(progress);

        if (elapsed >= DWELL_THRESHOLD && !combineReady) {
          setCombineReady(true);
        }
      }
    }, 30);

    return () => {
      if (dwellIntervalRef.current) {
        clearInterval(dwellIntervalRef.current);
        dwellIntervalRef.current = null;
      }
    };
  }, [activeId, inCombineZone, overId, combineReady, DWELL_THRESHOLD]);

  // Handle drag move - detect zone (center = combine, edge = reorder)
  const handleDragMove = useCallback(event => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setOverId(null);
      setInCombineZone(false);
      return;
    }

    const newOverId = over.id;
    const targetChanged = newOverId !== overId;

    // Find the target element to calculate zones
    const targetElement = document.querySelector(`[data-id="${newOverId}"]`);

    if (!targetElement) {
      setOverId(newOverId);
      setInCombineZone(false);
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    const mouseX = mousePositionRef.current.x;

    // Calculate relative position: 0 = left edge, 1 = right edge
    const relativeX = (mouseX - rect.left) / rect.width;

    // Center zone (middle 50%) = combine, Edge zones (outer 25% each) = reorder
    const isInCenter = relativeX > 0.25 && relativeX < 0.75;

    setOverId(newOverId);

    // If zone changed or target changed, update state
    if (isInCenter !== inCombineZone || targetChanged) {
      setInCombineZone(isInCenter);

      if (!isInCenter || targetChanged) {
        // Entering reorder zone or new target - reset dwell
        setDwellProgress(0);
        setCombineReady(false);
        dwellStartTimeRef.current = null;
      } else if (isInCenter && !dwellStartTimeRef.current) {
        // Entering combine zone - start dwell timer
        dwellStartTimeRef.current = Date.now();
      }
    }
  }, [overId, inCombineZone]);

  // Animate drag overlay entry
  useEffect(() => {
    if (activeId) {
      // Small delay to allow DOM to update, then trigger animation
      const timer = setTimeout(() => {
        setDragOverlayAnimated(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setDragOverlayAnimated(false);
    }
  }, [activeId]);

  // Handle drag end - position-based: combineReady=true means combine, otherwise reorder
  const handleDragEnd = async event => {
    const { active, over } = event;

    // Capture combineReady before clearing state
    const shouldCombine = combineReady;

    // Clear drag state
    setActiveId(null);
    setOverId(null);
    setShowDragOverlay(false);
    setCombineReady(false);
    setInCombineZone(false);
    setDwellProgress(0);

    if (!over || active.id === over.id) {
      return;
    }

    const draggedId = active.id;
    const targetId = over.id;
    const activeIsStack = draggedId.toString().startsWith('stack-');
    const overIsStack = targetId.toString().startsWith('stack-');

    // ========================================
    // COMBINE MODE - User held still, wants to create/merge stacks
    // ========================================
    if (shouldCombine) {
      // Stack onto stack - show combine modal
      if (activeIsStack && overIsStack) {
        const draggedStackId = draggedId.toString().replace('stack-', '');
        const targetStackId = targetId.toString().replace('stack-', '');
        const draggedStack = stacks.find(s => s.id.toString() === draggedStackId);
        const targetStack = stacks.find(s => s.id.toString() === targetStackId);

        if (draggedStack && targetStack) {
          setCombiningStacks({ stack1: draggedStack, stack2: targetStack });
          setIsCombineStacksModalOpen(true);
        }
        return;
      }

      // Project onto stack - add to stack
      if (!activeIsStack && overIsStack) {
        const draggedProject = projects.find(p => p.id.toString() === draggedId.toString());
        const stackIdStr = targetId.toString().replace('stack-', '');
        const stackId = isNaN(parseInt(stackIdStr)) ? stackIdStr : parseInt(stackIdStr);
        const targetStack = stacks.find(stack => stack.id == stackId);

        if (draggedProject && targetStack) {
          const updatedProject = { ...draggedProject, isPinned: targetStack.isPinned };
          const updatedStacks = stacks.map(stack => {
            if (stack.id == stackId) {
              const newProjects = [...stack.projects, updatedProject];
              return {
                ...stack,
                projects: newProjects,
                projectIds: [...(stack.projectIds || stack.projects.map(p => p.id)), draggedProject.id],
                // Recalculate compliance immediately for the updated stack
                complianceSummary: calculateStackCompliance(newProjects)
              };
            }
            return stack;
          });
          setStacks(updatedStacks);
          setProjects(projects.filter(p => p.id !== draggedProject.id));
        }
        return;
      }

      // Project onto project - create new stack
      if (!activeIsStack && !overIsStack) {
        const draggedProject = projects.find(p => p.id.toString() === draggedId.toString());
        const targetProject = projects.find(p => p.id.toString() === targetId.toString());

        if (draggedProject && targetProject) {
          // Check if target is already in a stack
          const existingStack = stacks.find(stack => stack.projects.some(p => p.id === targetProject.id));

          if (existingStack) {
            // Add to existing stack
            const updatedProject = { ...draggedProject, isPinned: existingStack.isPinned };
            const updatedStacks = stacks.map(stack => {
              if (stack.id === existingStack.id) {
                const newProjects = [...stack.projects, updatedProject];
                return {
                  ...stack,
                  projects: newProjects,
                  projectIds: [...(stack.projectIds || stack.projects.map(p => p.id)), draggedProject.id],
                  // Recalculate compliance immediately for the updated stack
                  complianceSummary: calculateStackCompliance(newProjects)
                };
              }
              return stack;
            });
            setStacks(updatedStacks);
            setProjects(projects.filter(p => p.id !== draggedProject.id));
          } else {
            // Show settings modal for new stack
            setPendingStackProjects([targetProject, draggedProject]);
            setIsStackSettingsOpen(true);
          }
        }
        return;
      }

      // Stack onto project - add stack's projects to a new stack with the target project
      if (activeIsStack && !overIsStack) {
        const draggedStackId = draggedId.toString().replace('stack-', '');
        const draggedStack = stacks.find(s => s.id.toString() === draggedStackId);
        const targetProject = projects.find(p => p.id.toString() === targetId.toString());

        if (draggedStack && targetProject) {
          // Create new stack with target project + all projects from dragged stack
          setPendingStackProjects([targetProject, ...draggedStack.projects]);
          setIsStackSettingsOpen(true);
          // Remove the old stack
          setStacks(stacks.filter(s => s.id.toString() !== draggedStackId));
        }
        return;
      }
    }

    // ========================================
    // REORDER MODE - Unified reordering for projects and stacks mixed together
    // ========================================

    // Get unpinned items
    const unpinnedProjects = projects.filter(p => !p.isPinned);
    const unpinnedStacks = stacks.filter(s => !s.isPinned);

    // Build combined order: use existing combinedOrder or derive from current items
    let currentCombinedOrder = [...combinedOrder];

    // If combinedOrder is empty, initialize from current state
    if (currentCombinedOrder.length === 0) {
      // Add all unpinned projects
      unpinnedProjects.forEach(p => {
        currentCombinedOrder.push(p.id.toString());
      });
      // Add all unpinned stacks
      unpinnedStacks.forEach(s => {
        currentCombinedOrder.push(`stack-${s.id}`);
      });
    } else {
      // Ensure all current items are in the order
      unpinnedProjects.forEach(p => {
        const projectId = p.id.toString();
        if (!currentCombinedOrder.includes(projectId)) {
          currentCombinedOrder.push(projectId);
        }
      });
      unpinnedStacks.forEach(s => {
        const stackId = `stack-${s.id}`;
        if (!currentCombinedOrder.includes(stackId)) {
          currentCombinedOrder.push(stackId);
        }
      });

      // Remove items that no longer exist
      currentCombinedOrder = currentCombinedOrder.filter(itemId => {
        const isStack = itemId.toString().startsWith('stack-');
        if (isStack) {
          const stackId = itemId.toString().replace('stack-', '');
          return unpinnedStacks.some(s => s.id.toString() === stackId);
        } else {
          return unpinnedProjects.some(p => p.id.toString() === itemId.toString());
        }
      });
    }

    // Normalize the dragged and target IDs to match combinedOrder format
    const activeItemId = activeIsStack ? draggedId.toString() : draggedId.toString();
    const overItemId = overIsStack ? targetId.toString() : targetId.toString();

    const oldIndex = currentCombinedOrder.indexOf(activeItemId);
    const newIndex = currentCombinedOrder.indexOf(overItemId);

    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      const newOrder = arrayMove(currentCombinedOrder, oldIndex, newIndex);
      setCombinedOrder(newOrder);

      // Also update the legacy separate orders for backward compatibility
      const newProjectOrder = newOrder.filter(id => !id.toString().startsWith('stack-'));
      const newStackOrder = newOrder
        .filter(id => id.toString().startsWith('stack-'))
        .map(id => id.toString().replace('stack-', ''));
      setProjectOrder(newProjectOrder);
      setStackOrder(newStackOrder);
    }
  };

  // Handle stack creation/edit from modal
  const handleCreateStack = stackData => {
    if (editingStack) {
      // Find which projects are new (not already in stack)
      const existingProjectIds = editingStack.projects.map(p => p.id);
      const newProjectIds = stackData.projects.map(p => p.id);
      const addedProjectIds = newProjectIds.filter(
        id => !existingProjectIds.includes(id)
      );

      // Update the stack
      setStacks(prevStacks =>
        prevStacks.map(stack =>
          stack.id === editingStack.id
            ? {
                ...stack,
                name: stackData.name,
                image: stackData.image,
                projects: stackData.projects,
                projectIds: newProjectIds,
                // Recalculate compliance for the updated projects
                complianceSummary: calculateStackCompliance(stackData.projects)
              }
            : stack
        )
      );

      // Remove any newly added projects from the individual projects list
      if (addedProjectIds.length > 0) {
        setProjects(prevProjects =>
          prevProjects.filter(p => !addedProjectIds.includes(p.id))
        );
      }

      setEditingStack(null);
    } else {
      // Creating new stack
      // Inherit pinned status from the first project (they should all have the same section)
      // If projects have different pinned status, prioritize pinned=true
      const shouldBePinned = stackData.projects.some(p => p.isPinned);

      const newStack = {
        id: Date.now(), // Use timestamp as ID for new stacks
        name: stackData.name,
        image: stackData.image,
        projects: stackData.projects,
        projectIds: stackData.projects.map(p => p.id),
        createdAt: new Date(),
        isPinned: shouldBePinned, // Inherit pinned status
        // Calculate compliance immediately for the new stack
        complianceSummary: calculateStackCompliance(stackData.projects)
      };

      // Add new stack at the FRONT (not the end)
      setStacks([newStack, ...stacks]);

      // Remove projects from individual projects list
      const projectIds = stackData.projects.map(p => p.id);
      const updatedProjects = projects.filter(p => !projectIds.includes(p.id));
      setProjects(updatedProjects);

      // Update combinedOrder: remove the project IDs that are now in the stack, add stack at front
      setCombinedOrder(prevOrder => {
        const newStackId = `stack-${newStack.id}`;
        // Remove project IDs that are now in the stack
        const filteredOrder = prevOrder.filter(id => !projectIds.includes(id.toString()) && !projectIds.map(String).includes(id));
        // Add new stack at the front
        return [newStackId, ...filteredOrder];
      });

      setPendingStackProjects(null);
    }
  };

  // Handle empty stack (move projects back to individual projects)
  const handleEmptyStack = () => {
    if (pendingStackProjects) {
      // Don't create the stack, just close the modal
      setPendingStackProjects(null);
      setIsStackSettingsOpen(false);
    }
  };

  // Handle update stack
  const handleUpdateStack = (stackId, updates) => {
    setStacks(prevStacks =>
      prevStacks.map(stack =>
        stack.id === stackId ? { ...stack, ...updates } : stack
      )
    );
    // The stacks will be saved to Supabase automatically via the useEffect that watches stacks
  };

  // Handle delete stack
  const handleDeleteStack = stackId => {
    setStacks(prevStacks => prevStacks.filter(stack => stack.id !== stackId));
  };

  // Handle disband stack - move all projects back to individual projects and delete stack
  const handleDisbandStack = stackId => {
    const stackToDisband = stacks.find(stack => stack.id === stackId);
    if (!stackToDisband) {
      return;
    }

    // Phase 1: Mark stack as disbanding and move to center
    setDisbandingStackId(stackId);

    // Phase 2: After stack moves to center, remove it and show cards
    setTimeout(() => {
      // Center position for cards to start from
      const centerX = 490; // Center of max-w-7xl container
      const centerY = 200;

      // Remove stack and show falling cards with proper pinned status
      const projectsToMoveBack = (stackToDisband.projects || []).map(p => ({
        ...p,
        isPinned: stackToDisband.isPinned || false
      }));

      // Calculate final positions by simulating the sort
      const combined = [...projects, ...projectsToMoveBack];

      // Apply the current filter's sort logic
      let sorted;
      switch (activeFilter) {
        case 'a-z':
          sorted = combined.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'z-a':
          sorted = combined.sort((a, b) => b.name.localeCompare(a.name));
          break;
        case 'projects':
          sorted = combined.sort((a, b) => a.name.localeCompare(b.name));
          break;
        default: // 'all' and others - sort by created date (newest first)
          sorted = combined.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.created_date);
            const dateB = new Date(b.createdAt || b.created_date);
            return dateB - dateA;
          });
      }

      // Split into pinned and unpinned for position calculation
      const pinnedSorted = sorted.filter(p => p.isPinned);
      const unpinnedSorted = sorted.filter(p => !p.isPinned);

      // Calculate final grid positions for each project within its section
      const projectsWithPositions = projectsToMoveBack.map(p => {
        const sectionArray = p.isPinned ? pinnedSorted : unpinnedSorted;
        const gridIndex = sectionArray.findIndex(sp => sp.id === p.id);
        return {
          ...p,
          finalPosition: gridIndex,
          stackStartX: centerX,
          stackStartY: centerY
        };
      });

      setStacks(prevStacks => prevStacks.filter(stack => stack.id !== stackId));
    }, 400); // Wait for stack to reach center (matches animation duration)

    // Phase 3: Clear animation state FIRST to prevent duplicates
    setTimeout(() => {
      setDisbandingStackId(null);
    }, 750);

    // Phase 4: After animation state cleared, add projects to main list
    setTimeout(() => {
      // Projects should retain the stack's pinned status
      const projectsToMoveBack = (stackToDisband.projects || []).map(p => ({
        ...p,
        isPinned: stackToDisband.isPinned || false
      }));

      // Add projects back to the main projects list (only if not already present)
      setProjects(prevProjects => {
        const existingIds = prevProjects.map(p => p.id);
        const newProjects = projectsToMoveBack.filter(
          p => !existingIds.includes(p.id)
        );
        const combined = [...prevProjects, ...newProjects];

        // Clean up projectOrder to remove duplicates
        setProjectOrder(prevOrder => [...new Set(prevOrder)]);

        // Update combinedOrder: replace stack with its projects at the same position
        setCombinedOrder(prevOrder => {
          const stackIdStr = `stack-${stackId}`;
          const stackIndex = prevOrder.indexOf(stackIdStr);
          if (stackIndex !== -1) {
            // Insert project IDs at the stack's position
            const projectIdStrs = newProjects.map(p => p.id.toString());
            return [
              ...prevOrder.slice(0, stackIndex),
              ...projectIdStrs,
              ...prevOrder.slice(stackIndex + 1)
            ];
          }
          // Stack wasn't in order, just remove it and add projects at front
          return [
            ...newProjects.map(p => p.id.toString()),
            ...prevOrder.filter(id => id !== stackIdStr)
          ];
        });

        // Sort according to activeFilter to maintain consistent ordering
        let sorted;
        switch (activeFilter) {
          case 'a-z':
            sorted = combined.sort((a, b) => a.name.localeCompare(b.name));
            break;
          case 'z-a':
            sorted = combined.sort((a, b) => b.name.localeCompare(a.name));
            break;
          case 'projects':
            sorted = combined.sort((a, b) => a.name.localeCompare(b.name));
            break;
          default: // 'all' and others - sort by created date (newest first)
            sorted = combined.sort((a, b) => {
              const dateA = new Date(a.createdAt || a.created_date);
              const dateB = new Date(b.createdAt || b.created_date);
              return dateB - dateA;
            });
        }

        return sorted;
      });
    }, 800);
  };

  // Handle stack card click - navigate to stack combined view - memoized
  const handleStackClick = useCallback(stack => {
    navigate(`/stack/${stack.id}`);
  }, [navigate]);

  // Handle stack edit - open modal with stack data - memoized
  const handleStackEdit = useCallback(stack => {
    setEditingStack(stack);
    setIsStackSettingsOpen(true);
  }, []);

  // Handle project edit details - memoized
  const handleProjectEditDetails = useCallback(project => {
    setEditingProject(project);
    setProjectSettingsMode('details');
    setIsProjectSettingsOpen(true);
  }, []);

  // Handle project edit access - memoized
  const handleProjectEditAccess = useCallback(project => {
    setEditingProject(project);
    setProjectSettingsMode('access');
    setIsProjectSettingsOpen(true);
  }, []);

  // Handle project update from modal - memoized
  const handleUpdateProject = useCallback(async updatedProject => {
    try {
      // Update the project in the local state
      setProjects(prevProjects =>
        prevProjects.map(p =>
          p.id === updatedProject.id ? { ...p, ...updatedProject } : p
        )
      );

      // Close the modal
      setIsProjectSettingsOpen(false);
      setEditingProject(null);
    } catch (error) {
      console.error('Error updating project:', error);
    }
  }, []);

  // Handle combining two stacks - memoized
  const handleCombineStacks = useCallback(resultStack => {
    setCombiningStacks(prevCombining => {
      const { stack1, stack2 } = prevCombining;

      if (!stack1 || !stack2 || !resultStack) return prevCombining;

      // If resultStack already has the combined projects (new stack option), use it directly
      if (
        resultStack.projects &&
        resultStack.projects.length ===
          stack1.projects.length + stack2.projects.length
      ) {
        // New stack with custom name/image - replace stack1, remove stack2
        // Recalculate compliance for the combined stack
        const stackWithCompliance = {
          ...resultStack,
          complianceSummary: calculateStackCompliance(resultStack.projects)
        };
        setStacks(prevStacks =>
          prevStacks
            .map(stack => (stack.id === stack1.id ? stackWithCompliance : stack))
            .filter(stack => stack.id !== stack2.id)
        );
      } else {
        // Using existing stack - determine which to keep and which to remove
        const keepStack = resultStack;
        const removeStack = keepStack.id === stack1.id ? stack2 : stack1;

        // Combine all projects from both stacks
        const combinedProjects = [...stack1.projects, ...stack2.projects];
        const combinedProjectIds = combinedProjects.map(p => p.id);

        // Update the kept stack with combined projects
        setStacks(
          prevStacks =>
            prevStacks
              .map(stack =>
                stack.id === keepStack.id
                  ? {
                      ...stack,
                      name: keepStack.name,
                      image: keepStack.image,
                      projects: combinedProjects,
                      projectIds: combinedProjectIds,
                      // Recalculate compliance for the combined stack
                      complianceSummary: calculateStackCompliance(combinedProjects)
                    }
                  : stack
              )
              .filter(stack => stack.id !== removeStack.id) // Remove the other stack
        );
      }

      // Clear the combining state
      return { stack1: null, stack2: null };
    });
  }, []);

  // Handle project color change - memoized
  const handleProjectColorChange = useCallback((projectId, color) => {
    setProjects(prevProjects =>
      prevProjects.map(project =>
        project.id === projectId ? { ...project, color } : project
      )
    );

    // Also update stacks if the project is in a stack
    setStacks(prevStacks =>
      prevStacks.map(stack => ({
        ...stack,
        projects: stack.projects.map(project =>
          project.id === projectId ? { ...project, color } : project
        )
      }))
    );
  }, []);

  // Handle project pinning - memoized
  const handleProjectPin = useCallback(project => {
    // Toggle pin status on the project itself
    const updatedProject = { ...project, isPinned: !project.isPinned };

    // Update the project in the projects list
    setProjects(prevProjects =>
      prevProjects.map(p => (p.id === project.id ? updatedProject : p))
    );

    // Supabase will be updated automatically by the useEffect that watches projects
  }, []);

  // Handle stack pinning - memoized
  const handleStackPin = useCallback(stack => {
    // Toggle pin status on the stack
    setStacks(prevStacks =>
      prevStacks.map(s =>
        s.id === stack.id ? { ...s, isPinned: !s.isPinned } : s
      )
    );
  }, []);

  // Handle stack color change - memoized
  const handleStackColorChange = useCallback((stackId, color) => {
    setStacks(prevStacks =>
      prevStacks.map(stack =>
        stack.id === stackId ? { ...stack, color } : stack
      )
    );
  }, []);

  // Handle removing project from stack - memoized
  const handleRemoveFromStack = useCallback((stackId, projectToRemove) => {
    setStacks(prevStacks =>
      prevStacks.map(stack => {
        if (stack.id === stackId) {
          const updatedProjects = stack.projects.filter(
            p => p.id !== projectToRemove.id
          );
          return {
            ...stack,
            projects: updatedProjects,
            projectIds: updatedProjects.map(p => p.id),
            // Recalculate compliance for remaining projects
            complianceSummary: calculateStackCompliance(updatedProjects)
          };
        }
        return stack;
      })
    );

    // Add project back to individual projects list
    setProjects(prevProjects => [projectToRemove, ...prevProjects]);
  }, []);

  // Bulk Selection Mode Handlers - memoized
  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode(prev => {
      if (prev) {
        // Clear selection when exiting selection mode
        setSelectedSpaceIds([]);
      }
      return !prev;
    });
  }, []);

  const handleSpaceSelectionToggle = useCallback(spaceId => {
    setSelectedSpaceIds(prev => {
      if (prev.includes(spaceId)) {
        return prev.filter(id => id !== spaceId);
      } else {
        return [...prev, spaceId];
      }
    });
  }, []);

  const enterSelectionModeWithSpace = useCallback(spaceId => {
    setIsSelectionMode(true);
    setSelectedSpaceIds([spaceId]);
  }, []);

  const handleSelectAll = () => {
    // Get all visible project IDs from allSpaceItems (respecting current filter)
    const allVisibleIds = allSpaceItems
      .filter(item => item.type === 'project') // Only select projects, not stacks
      .map(item => item.id);

    setSelectedSpaceIds(allVisibleIds);
  };

  const handleCreateStackFromSelected = () => {
    if (selectedSpaceIds.length < 2) {
      alert('Please select at least 2 spaces to create a stack');
      return;
    }

    // Get the selected projects
    const selectedProjects = projects.filter(p =>
      selectedSpaceIds.includes(p.id)
    );

    // Also check projects in stacks
    const projectsInStacks = stacks.flatMap(stack =>
      stack.projects.filter(p => selectedSpaceIds.includes(p.id))
    );

    const allSelectedProjects = [...selectedProjects, ...projectsInStacks];

    if (allSelectedProjects.length < 2) {
      alert('Please select at least 2 spaces to create a stack');
      return;
    }

    // Open stack settings modal with selected projects
    setPendingStackProjects(allSelectedProjects);
    setIsStackSettingsOpen(true);

    // Clear selection and exit selection mode
    setSelectedSpaceIds([]);
    setIsSelectionMode(false);
  };

  const handleBulkDelete = () => {
    if (selectedSpaceIds.length === 0) return;
    setIsDeleteModalOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedSpaceIds.length === 0) return;

    setIsDeleting(true);

    try {
      // Chunk the IDs to avoid URL length limits (max 50 per request)
      const chunkSize = 50;
      const chunks = [];
      for (let i = 0; i < selectedSpaceIds.length; i += chunkSize) {
        chunks.push(selectedSpaceIds.slice(i, i + chunkSize));
      }

      // Delete chunks sequentially
      for (let i = 0; i < chunks.length; i++) {
        const { error } = await supabase
          .from('projects')
          .delete()
          .in('id', chunks[i]);

        if (error) {
          throw error;
        }
      }

      // Remove from local state
      setProjects(prev => prev.filter(p => !selectedSpaceIds.includes(p.id)));

      // Remove from stacks
      setStacks(prev =>
        prev
          .map(stack => ({
            ...stack,
            projects: stack.projects.filter(
              p => !selectedSpaceIds.includes(p.id)
            ),
            projectIds: stack.projectIds.filter(
              id => !selectedSpaceIds.includes(id)
            )
          }))
          .filter(stack => stack.projects.length > 0)
      ); // Remove empty stacks

      // Clear selection and exit selection mode
      setSelectedSpaceIds([]);
      setIsSelectionMode(false);
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error('Error deleting spaces:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkPin = () => {
    if (selectedSpaceIds.length === 0) return;

    // Check if all selected items are currently pinned
    const selectedProjects = projects.filter(p =>
      selectedSpaceIds.includes(p.id)
    );
    const selectedStacks = stacks.filter(s => selectedSpaceIds.includes(s.id));
    const allPinned = [...selectedProjects, ...selectedStacks].every(
      item => item.isPinned
    );

    // Toggle pin status for all selected items
    const newPinStatus = !allPinned;

    // Update projects
    setProjects(prevProjects =>
      prevProjects.map(p =>
        selectedSpaceIds.includes(p.id) ? { ...p, isPinned: newPinStatus } : p
      )
    );

    // Update stacks
    setStacks(prevStacks =>
      prevStacks.map(s =>
        selectedSpaceIds.includes(s.id) ? { ...s, isPinned: newPinStatus } : s
      )
    );
  };

  // Bulk Archive
  const handleBulkArchive = async () => {
    if (selectedSpaceIds.length === 0) return;

    try {
      // Find the spaces being archived
      const spacesToArchive = projects.filter(p =>
        selectedSpaceIds.includes(p.id)
      );
      const stacksToArchive = stacks.filter(s =>
        selectedSpaceIds.includes(s.id)
      );

      // Optimistically update UI - move to archived state immediately
      spacesToArchive.forEach(space => {
        space.is_archived = true;
      });
      stacksToArchive.forEach(stack => {
        stack.is_archived = true;
      });

      setArchivedSpaces(prev => [...prev, ...spacesToArchive]);
      setArchivedStacks(prev => [...prev, ...stacksToArchive]);
      setProjects(prev => prev.filter(p => !selectedSpaceIds.includes(p.id)));
      setStacks(prev => prev.filter(s => !selectedSpaceIds.includes(s.id)));

      // Clear selection and exit selection mode
      setSelectedSpaceIds([]);
      setIsSelectionMode(false);

      // Update database in background
      const { error } = await supabase
        .from('projects')
        .update({ is_archived: true })
        .in('id', selectedSpaceIds);

      if (error) throw error;
    } catch (error) {
      console.error('Bulk archive error:', error);
      setDashboardError('Failed to archive spaces');
      // Revert optimistic update on error
      setArchivedLoaded(false); // Force reload of archived data
    }
  };

  // Bulk Restore
  const handleBulkRestore = async () => {
    if (selectedSpaceIds.length === 0) return;

    try {
      // Find the spaces being restored
      const spacesToRestore = archivedSpaces.filter(p =>
        selectedSpaceIds.includes(p.id)
      );
      const stacksToRestore = archivedStacks.filter(s =>
        selectedSpaceIds.includes(s.id)
      );

      // Optimistically update UI - move to active state immediately
      spacesToRestore.forEach(space => {
        space.is_archived = false;
      });
      stacksToRestore.forEach(stack => {
        stack.is_archived = false;
      });

      setProjects(prev => [...prev, ...spacesToRestore]);
      setStacks(prev => [...prev, ...stacksToRestore]);
      setArchivedSpaces(prev =>
        prev.filter(p => !selectedSpaceIds.includes(p.id))
      );
      setArchivedStacks(prev =>
        prev.filter(s => !selectedSpaceIds.includes(s.id))
      );

      // Clear selection and exit selection mode
      setSelectedSpaceIds([]);
      setIsSelectionMode(false);

      // Update database in background
      const { error } = await supabase
        .from('projects')
        .update({ is_archived: false })
        .in('id', selectedSpaceIds);

      if (error) throw error;
    } catch (error) {
      console.error('Bulk restore error:', error);
      setDashboardError('Failed to restore spaces');
      // Revert optimistic update on error
      setArchivedLoaded(false); // Force reload of archived data
    }
  };

  // Archive a space (individual)
  const handleArchive = async spaceId => {
    try {
      // Find the space being archived
      const spaceToArchive =
        projects.find(p => p.id === spaceId) ||
        stacks.find(s => s.id === spaceId);
      if (!spaceToArchive) return;

      // Optimistically update UI
      spaceToArchive.is_archived = true;

      if (projects.find(p => p.id === spaceId)) {
        setArchivedSpaces(prev => [...prev, spaceToArchive]);
        setProjects(prev => prev.filter(p => p.id !== spaceId));
      } else {
        setArchivedStacks(prev => [...prev, spaceToArchive]);
        setStacks(prev => prev.filter(s => s.id !== spaceId));
      }

      // Update database in background
      const { error } = await supabase
        .from('projects')
        .update({ is_archived: true })
        .eq('id', spaceId);

      if (error) throw error;
    } catch (error) {
      console.error('Archive error:', error);
      setDashboardError('Failed to archive space');
      setArchivedLoaded(false); // Force reload
    }
  };

  // Restore a space from archive (individual)
  const handleRestore = async spaceId => {
    try {
      // Find the space being restored
      const spaceToRestore =
        archivedSpaces.find(p => p.id === spaceId) ||
        archivedStacks.find(s => s.id === spaceId);
      if (!spaceToRestore) return;

      // Optimistically update UI
      spaceToRestore.is_archived = false;

      if (archivedSpaces.find(p => p.id === spaceId)) {
        setProjects(prev => [...prev, spaceToRestore]);
        setArchivedSpaces(prev => prev.filter(p => p.id !== spaceId));
      } else {
        setStacks(prev => [...prev, spaceToRestore]);
        setArchivedStacks(prev => prev.filter(s => s.id !== spaceId));
      }

      // Update database in background
      const { error } = await supabase
        .from('projects')
        .update({ is_archived: false })
        .eq('id', spaceId);

      if (error) throw error;
    } catch (error) {
      console.error('Restore error:', error);
      setDashboardError('Failed to restore space');
      setArchivedLoaded(false); // Force reload
    }
  };

  // Show loading state only for initial auth loading
  if (loading) {
    return (
      <AppShell3 breadcrumbs={breadcrumbs} pageTitle='' pageDescription=''>
        <div className='flex items-center justify-center min-h-96'>
          <div className='text-center'>
            <LoadingSpinner />
            <p className='text-muted-foreground mt-4'>
              Loading authentication...
            </p>
          </div>
        </div>
      </AppShell3>
    );
  }

  // Show minimal loading while dashboard data loads
  if (!dashboardLoaded && user && company && userRole) {
    return (
      <AppShell3 breadcrumbs={breadcrumbs} pageTitle='' pageDescription=''>
        <div className='flex items-center justify-center min-h-96'>
          <div className='text-center'>
            <LoadingSpinner />
            <p className='text-muted-foreground mt-4'>Loading dashboard...</p>
            <p className='text-xs text-gray-500 mt-2'>
              Company: {company?.name || 'Loading'} | Role:{' '}
              {userRole || 'Loading...'}
            </p>
          </div>
        </div>
      </AppShell3>
    );
  }

  // Show error state
  if (dashboardError) {
    return (
      <AppShell3 breadcrumbs={breadcrumbs} pageTitle='' pageDescription=''>
        <div className='flex items-center justify-center min-h-96'>
          <div className='text-center'>
            <p className='text-red-600 mb-4'>{dashboardError}</p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        </div>
      </AppShell3>
    );
  }

  // Don't render dashboard until data is loaded
  if (!dashboardLoaded) {
    return (
      <AppShell3 breadcrumbs={breadcrumbs} pageTitle='' pageDescription=''>
        <div className='flex items-center justify-center min-h-96'>
          <div className='text-center'>
            <LoadingSpinner />
            <p className='text-muted-foreground mt-4'>Loading spaces...</p>
          </div>
        </div>
      </AppShell3>
    );
  }

  return (
    <AppShell3
      breadcrumbs={breadcrumbs}
      pageTitle=''
      pageDescription=''
      onSearchClick={() => setSearchOpen(true)}
      onCreateSpace={() => navigate('/create-space')}
    >
      {/* Dashboard Subnav */}
      <DashboardSubnav
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onCreateSpace={() => navigate('/create-space')}
        onImportExport={() => setIsImportExportModalOpen(true)}
        onInvitePeople={() => setIsInviteModalOpen(true)}
        canCreateSpaces={permissions['spaces.create']}
        canInviteUsers={permissions['users.invite']}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onGridClick={() => {
          // Easter egg: Click grid 10 times to trigger loader
          gridClickCount.current++;

          // Reset counter if too much time passes between clicks
          clearTimeout(gridClickTimer.current);
          gridClickTimer.current = setTimeout(() => {
            gridClickCount.current = 0;
          }, 2000);

          // Trigger easter egg at 10 clicks
          if (gridClickCount.current === 10) {
            setShowEasterEgg(true);
            gridClickCount.current = 0;

            // Auto-hide after 10 seconds
            setTimeout(() => {
              setShowEasterEgg(false);
            }, 10000);
          }
        }}
      />

      {/* Easter Egg Loader Overlay */}
      {showEasterEgg && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center'
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          }}
          onClick={() => setShowEasterEgg(false)}
        >
          <div className='text-center'>
            <div className='bg-white rounded-2xl p-8 shadow-2xl'>
              <LoadingSpinner size={25} />
            </div>
            <p className='text-white mt-6 text-lg font-medium'>
              Training Matrix
            </p>
            <p className='text-white/80 text-sm mt-2'>
              You found the easter egg! 🎉
            </p>
          </div>
        </div>
      )}

      <div className='max-w-7xl mx-auto pt-6'>
        <ProjectCreationModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreateProject={handleCreateProject}
        />

        {/* Drag and Drop Context - wraps both sections */}
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
        >
          {/* Pinned Section - Only show in grid view */}
          {viewMode === 'grid' && pinnedItems.length > 0 && (
            <div className='mb-8'>
              <div className='flex justify-center mb-4'>
                <div className='bg-green-200 text-green-700 text-xs font-medium px-3 py-1 rounded-sm'>
                  PINNED
                </div>
              </div>

              <div className='flex justify-center'>
                {/* Wrap pinned section in SortableContext for reordering */}
                <SortableContext
                  items={pinnedItems.map(item =>
                    item.type === 'stack'
                      ? `stack-${item.id}`
                      : item.id.toString()
                  )}
                >
                  <div className='flex flex-wrap justify-center gap-2 w-full max-w-7xl relative'>
                    {pinnedItems.map((item, index) => {
                      const row = Math.floor(index / 4);
                      const col = index % 4;
                      if (item.type === 'stack') {
                        const isDisbanding = disbandingStackId === item.id;
                        // Keep motion.div ONLY for disband animation, otherwise use plain div
                        return isDisbanding ? (
                          <motion.div
                            key={`stack-${item.id}`}
                            data-stack-id={item.id}
                            className='w-[calc(50%-8px)] sm:w-[300px]'
                            initial={{ opacity: 1, scale: 1 }}
                            animate={{
                              opacity: 0,
                              scale: 0.8,
                              x: 490 - col * 316, // Move to center X
                              y: 200 - row * 316 // Move to center Y
                            }}
                            transition={{
                              duration: 0.4,
                              ease: [0.25, 0.1, 0.25, 1]
                            }}
                          >
                            <SortableStackCard
                              stack={item}
                              isOver={overId === `stack-${item.id}`}
                              combineMode={inCombineZone}
                              dwellProgress={dwellProgress}
                              onClick={() => handleStackClick(item)}
                              onColorChange={handleStackColorChange}
                              onPin={handleStackPin}
                              onDisband={handleDisbandStack}
                              onEdit={handleStackEdit}
                              isDisbanding={isDisbanding}
                            />
                          </motion.div>
                        ) : (
                          <div
                            key={`stack-${item.id}`}
                            data-stack-id={item.id}
                            className='w-[calc(50%-8px)] sm:w-[300px]'
                          >
                            <SortableStackCard
                              stack={item}
                              isOver={overId === `stack-${item.id}`}
                              combineMode={inCombineZone}
                              dwellProgress={dwellProgress}
                              onClick={() => handleStackClick(item)}
                              onColorChange={handleStackColorChange}
                              onPin={handleStackPin}
                              onDisband={handleDisbandStack}
                              onEdit={handleStackEdit}
                              isDisbanding={isDisbanding}
                            />
                          </div>
                        );
                      } else {
                        return (
                          <div
                            key={`project-${item.id}`}
                            className='w-[calc(50%-8px)] sm:w-[300px]'
                          >
                            <SortableProjectCard
                              project={item}
                              isOver={overId === item.id.toString()}
                              combineMode={inCombineZone}
                              dwellProgress={dwellProgress}
                              onColorChange={handleProjectColorChange}
                              onPin={handleProjectPin}
                              onEditDetails={handleProjectEditDetails}
                              onEditAccess={handleProjectEditAccess}
                              onArchive={
                                !item.is_archived ? handleArchive : undefined
                              }
                            />
                          </div>
                        );
                      }
                    })}
                  </div>
                </SortableContext>
              </div>
            </div>
          )}

          {/* Bulk Action Bar - Visible when in selection mode */}
          {isSelectionMode && (
            <div className='mb-4 flex justify-center'>
              <div className='bg-blue-100 border border-blue-300 rounded-lg px-4 py-3 flex items-center gap-4'>
                {selectedSpaceIds.length > 0 ? (
                  <>
                    <span className='text-sm font-medium text-blue-900'>
                      {selectedSpaceIds.length} space
                      {selectedSpaceIds.length !== 1 ? 's' : ''} selected
                    </span>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={handleCreateStackFromSelected}
                      className='h-8 bg-white'
                      disabled={selectedSpaceIds.length < 2}
                    >
                      Create Stack
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={handleBulkPin}
                      className='h-8 bg-white'
                    >
                      <Pin className='h-4 w-4 mr-1' />
                      {(() => {
                        const selectedProjects = projects.filter(p =>
                          selectedSpaceIds.includes(p.id)
                        );
                        const selectedStacks = stacks.filter(s =>
                          selectedSpaceIds.includes(s.id)
                        );
                        const allPinned = [
                          ...selectedProjects,
                          ...selectedStacks
                        ].every(item => item.isPinned);
                        return allPinned ? 'Unpin' : 'Pin';
                      })()}
                    </Button>
                    {activeFilter === 'archived' ? (
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={handleBulkRestore}
                        className='h-8 bg-green-600 text-white hover:bg-green-700 hover:text-white border-green-600'
                      >
                        <RotateCcw className='h-4 w-4 mr-1' />
                        Restore Selected
                      </Button>
                    ) : (
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={handleBulkArchive}
                        className='h-8 bg-white text-orange-600 hover:bg-orange-50 border-orange-300'
                      >
                        <Archive className='h-4 w-4 mr-1' />
                        Archive Selected
                      </Button>
                    )}
                  </>
                ) : (
                  <span className='text-sm font-medium text-blue-900'>
                    Select spaces to create a stack or perform bulk actions
                  </span>
                )}
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleSelectAll}
                  className='h-8 bg-white'
                >
                  Select All
                </Button>
              </div>
            </div>
          )}

          {/* All Spaces - FilterBar always visible, content conditional */}
          <div className='mb-8'>
            <div className='flex flex-col items-center gap-3 mb-4'>
              {/* Chip */}
              <div className='bg-stone-200 text-stone-700 text-xs font-medium px-3 py-1 rounded-sm'>
                ALL SPACES
              </div>

              {/* Filter Bar with Select button - Always visible so users can switch filters */}
              <div className='flex justify-center items-center gap-3'>
                <FilterBar
                  activeFilter={activeFilter}
                  onFilterChange={setActiveFilter}
                  viewMode={viewMode}
                  isSelectionMode={isSelectionMode}
                  onToggleSelectionMode={toggleSelectionMode}
                />
                {/* Restore All button - only visible when viewing archived spaces */}
                {activeFilter === 'archived' && allSpaceItems.length > 0 && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={async () => {
                      try {
                        const allIds = allSpaceItems.map(item => item.id);

                        // Find all archived spaces and stacks to restore
                        const spacesToRestore = archivedSpaces.filter(p =>
                          allIds.includes(p.id)
                        );
                        const stacksToRestore = archivedStacks.filter(s =>
                          allIds.includes(s.id)
                        );

                        // Optimistically update UI - move to active state immediately
                        spacesToRestore.forEach(space => {
                          space.is_archived = false;
                        });
                        stacksToRestore.forEach(stack => {
                          stack.is_archived = false;
                        });

                        setProjects(prev => [...prev, ...spacesToRestore]);
                        setStacks(prev => [...prev, ...stacksToRestore]);
                        setArchivedSpaces([]); // Clear all archived spaces
                        setArchivedStacks([]); // Clear all archived stacks

                        // Update database in background
                        const { error } = await supabase
                          .from('projects')
                          .update({ is_archived: false })
                          .in('id', allIds);

                        if (error) throw error;
                      } catch (error) {
                        console.error('Restore all error:', error);
                        setDashboardError('Failed to restore all spaces');
                        // Force reload of archived data on error
                        setArchivedLoaded(false);
                      }
                    }}
                    className='h-8 bg-green-600 text-white hover:bg-green-700 hover:text-white border-green-600'
                  >
                    <RotateCcw className='h-4 w-4 mr-1' />
                    Restore All
                  </Button>
                )}
              </div>
            </div>

            {/* Check if we have any items - only content is conditional */}
            {(viewMode === 'list' ||
              allSpaceItems.length > 0 ||
              projects.length === 0) &&
            allSpaceItems.length > 0 ? (
              <>
                {viewMode === 'grid' ? (
                  /* Grid view - Centered flex layout with max 4 cards per row */
                  <div className='flex justify-center'>
                    {/* Wrap unpinned section in SortableContext for reordering */}
                    <SortableContext
                      items={allSpaceItems.map(item =>
                        item.type === 'stack'
                          ? `stack-${item.id}`
                          : item.id.toString()
                      )}
                    >
                      <div className='flex flex-wrap justify-center gap-2 w-full max-w-7xl relative'>
                        {/* Render items with sortable cards */}
                        {allSpaceItems.map((item, index) => {
                          const row = Math.floor(index / 4);
                          const col = index % 4;
                          if (item.type === 'stack') {
                            const isDisbanding = disbandingStackId === item.id;
                            // Keep motion.div ONLY for disband animation, otherwise use plain div
                            return isDisbanding ? (
                              <motion.div
                                key={`stack-${item.id}`}
                                data-stack-id={item.id}
                                className='w-[calc(50%-8px)] sm:w-[300px]'
                                initial={{ opacity: 1, scale: 1 }}
                                animate={{
                                  opacity: 0,
                                  scale: 0.8,
                                  x: 490 - col * 316, // Move to center X
                                  y: 200 - row * 316 // Move to center Y
                                }}
                                transition={{
                                  duration: 0.4,
                                  ease: [0.25, 0.1, 0.25, 1]
                                }}
                              >
                                <SortableStackCard
                                  stack={item}
                                  isOver={overId === `stack-${item.id}`}
                                  combineMode={inCombineZone}
                                  dwellProgress={dwellProgress}
                                  onClick={() => handleStackClick(item)}
                                  onColorChange={handleStackColorChange}
                                  onPin={handleStackPin}
                                  onDisband={handleDisbandStack}
                                  onEdit={handleStackEdit}
                                  isDisbanding={isDisbanding}
                                />
                              </motion.div>
                            ) : (
                              <div
                                key={`stack-${item.id}`}
                                data-stack-id={item.id}
                                className='w-[calc(50%-8px)] sm:w-[300px]'
                              >
                                <SortableStackCard
                                  stack={item}
                                  isOver={overId === `stack-${item.id}`}
                                  combineMode={inCombineZone}
                                  dwellProgress={dwellProgress}
                                  onClick={() => handleStackClick(item)}
                                  onColorChange={handleStackColorChange}
                                  onPin={handleStackPin}
                                  onDisband={handleDisbandStack}
                                  onEdit={handleStackEdit}
                                  isDisbanding={isDisbanding}
                                />
                              </div>
                            );
                          } else {
                            return (
                              <div
                                key={`project-${item.id}`}
                                className='w-[calc(50%-8px)] sm:w-[300px]'
                              >
                                <SortableProjectCard
                                  project={item}
                                  isOver={overId === item.id.toString()}
                                  combineMode={inCombineZone}
                                  dwellProgress={dwellProgress}
                                  onColorChange={handleProjectColorChange}
                                  onPin={handleProjectPin}
                                  onEditDetails={handleProjectEditDetails}
                                  onEditAccess={handleProjectEditAccess}
                                  isSelectionMode={isSelectionMode}
                                  isSelected={selectedSpaceIds.includes(
                                    item.id
                                  )}
                                  onSelectionToggle={handleSpaceSelectionToggle}
                                  onEnterSelectionMode={
                                    enterSelectionModeWithSpace
                                  }
                                  onRestore={
                                    item.is_archived ? handleRestore : undefined
                                  }
                                  showRestoreButton={item.is_archived === true}
                                  onArchive={
                                    !item.is_archived
                                      ? handleArchive
                                      : undefined
                                  }
                                />
                              </div>
                            );
                          }
                        })}
                      </div>
                    </SortableContext>
                  </div>
                ) : (
                  /* List view */
                  <div className='max-w-7xl mx-auto'>
                    <div className='space-y-2'>
                      {allSpaceItems.map(item => (
                        <div
                          key={`${item.type}-${item.id}`}
                          className='bg-background rounded-lg border'
                        >
                          <ProjectListItem
                            item={item}
                            onColorChange={
                              item.type === 'stack'
                                ? handleStackColorChange
                                : handleProjectColorChange
                            }
                            onPin={
                              item.type === 'stack'
                                ? handleStackPin
                                : handleProjectPin
                            }
                            onClick={
                              item.type === 'stack' ? handleStackClick : null
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Load More button */}
                {hasMore && (
                  <div className='h-20 flex items-center justify-center'>
                    {isLoadingMore ? (
                      <div className='flex items-center gap-2 text-muted-foreground'>
                        <Spinner size="sm" />
                        <span className='text-sm'>Loading more spaces...</span>
                      </div>
                    ) : (
                      <Button
                        variant='outline'
                        onClick={loadMoreSpaces}
                        disabled={isLoadingMore}
                      >
                        Load More
                      </Button>
                    )}
                  </div>
                )}

                {/* End of list indicator */}
                {!hasMore && (
                  <div className='h-20 flex items-center justify-center'>
                    <span className='text-sm text-muted-foreground'>
                      All spaces loaded ({projects.length} total)
                    </span>
                  </div>
                )}
              </>
            ) : (
              /* Empty state for All Spaces section */
              <EmptySpacesState
                onCreateSpace={() => navigate('/create-space')}
                canCreateSpaces={permissions['spaces.create']}
                isArchiveView={activeFilter === 'archived'}
              />
            )}
          </div>

          {/* Drag Overlay for smooth drag animations */}
          <DragOverlay
            style={{ zIndex: 10000 }}
            dropAnimation={{
              duration: 200,
              easing: 'ease-out'
            }}
          >
            {showDragOverlay && activeId ? (
              <div
                className='transition-all duration-200 ease-out opacity-80'
                style={{
                  transform: dragOverlayAnimated
                    ? 'rotate(6deg) scale(1.1)'
                    : 'rotate(0deg) scale(1)'
                }}
              >
                {activeId.startsWith('stack-') ? (
                  <StackCard
                    stack={stacks.find(
                      s => s.id.toString() === activeId.replace('stack-', '')
                    )}
                    onClick={() => {}}
                    isDropTarget={false}
                  />
                ) : (
                  <ProjectCard
                    project={
                      getRecentProjects().find(
                        p => p.id.toString() === activeId
                      ) ||
                      getPinnedProjects().find(
                        p => p.id.toString() === activeId
                      )
                    }
                    onColorChange={() => {}}
                    onPin={() => {}}
                  />
                )}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Stack Settings Modal - Lazy mounted */}
      {isStackSettingsOpen && (
        <StackSettingsModal
          isOpen={isStackSettingsOpen}
          onClose={() => {
            setIsStackSettingsOpen(false);
            setPendingStackProjects(null);
            setEditingStack(null);
          }}
          projects={
            editingStack
              ? stacks.find(s => s.id === editingStack.id)?.projects || []
              : pendingStackProjects
          }
          stackName={
            editingStack
              ? stacks.find(s => s.id === editingStack.id)?.name ||
                editingStack.name
              : undefined
          }
          stackImage={
            editingStack
              ? stacks.find(s => s.id === editingStack.id)?.image ||
                editingStack.image
              : undefined
          }
          stackId={editingStack ? editingStack.id : undefined}
          onSave={handleCreateStack}
          onEmptyStack={handleEmptyStack}
          onDisband={
            editingStack ? () => handleDisbandStack(editingStack.id) : undefined
          }
          onRemoveProjectFromStack={handleRemoveFromStack}
        />
      )}

      {/* Stack View Modal - Lazy mounted */}
      {isStackViewOpen && (
        <StackViewModal
          isOpen={isStackViewOpen}
          onClose={() => {
            setIsStackViewOpen(false);
            setSelectedStack(null);
          }}
          stack={
            selectedStack ? stacks.find(s => s.id === selectedStack.id) : null
          }
          onRemoveFromStack={handleRemoveFromStack}
          onDeleteStack={handleDisbandStack}
          onUpdateStack={handleUpdateStack}
        />
      )}

      {/* Project Settings Modal - Lazy mounted */}
      {isProjectSettingsOpen && (
        <ProjectSettingsModal
          isOpen={isProjectSettingsOpen}
          onClose={() => {
            setIsProjectSettingsOpen(false);
            setEditingProject(null);
          }}
          project={editingProject}
          onUpdateProject={handleUpdateProject}
          initialMode={projectSettingsMode}
        />
      )}

      {/* Combine Stacks Modal - Lazy mounted */}
      {isCombineStacksModalOpen && (
        <CombineStacksModal
          isOpen={isCombineStacksModalOpen}
          onClose={() => {
            setIsCombineStacksModalOpen(false);
            setCombiningStacks({ stack1: null, stack2: null });
          }}
          stack1={combiningStacks.stack1}
          stack2={combiningStacks.stack2}
          onConfirm={handleCombineStacks}
        />
      )}

      {/* Quick Actions Command Menu - Keep mounted for keyboard shortcut */}
      <QuickActions
        onCreateSpace={() => navigate('/create-space')}
        canCreateSpaces={permissions['spaces.create']}
        open={searchOpen}
        setOpen={setSearchOpen}
      />

      {/* Invite Admin Modal - Lazy mounted */}
      {isInviteModalOpen && (
        <InviteAdminModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          companyId={company?.id}
          companyName={company?.name}
        />
      )}

      {/* Import/Export Modal - Lazy mounted */}
      {isImportExportModalOpen && (
        <SpacesImportExportModal
          isOpen={isImportExportModalOpen}
          onClose={() => setIsImportExportModalOpen(false)}
          projects={projects}
          onImport={handleImportSpaces}
        />
      )}

      {/* Bulk Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader className='space-y-3'>
            <div className='mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100'>
              <Trash2 className='h-6 w-6 text-red-600' />
            </div>
            <DialogTitle className='text-center'>Delete Spaces</DialogTitle>
            <DialogDescription className='text-center'>
              Are you sure you want to delete{' '}
              <strong>{selectedSpaceIds.length}</strong> space
              {selectedSpaceIds.length !== 1 ? 's' : ''}?
            </DialogDescription>
          </DialogHeader>

          <Alert className='border-red-200 bg-red-50'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            <AlertDescription className='text-red-800'>
              This action cannot be undone. This will permanently delete{' '}
              {selectedSpaceIds.length === 1
                ? 'the space'
                : 'all selected spaces'}{' '}
              and all associated data including matrices, team members, and
              settings.
            </AlertDescription>
          </Alert>

          <div className='flex gap-3 justify-end'>
            <Button
              variant='outline'
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={confirmBulkDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Spinner size="sm" className="mr-2 text-white" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className='h-4 w-4 mr-2' />
                  Delete {selectedSpaceIds.length} Space
                  {selectedSpaceIds.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell3>
  );
};

export default DashboardPage;
